import React from 'react';
import { useStudentTheme } from '@context/StudentThemeContext';
import { Sparkles, Terminal } from 'lucide-react';

const PythonBasicsReadingMaterial: React.FC = () => {
  const { theme } = useStudentTheme();
  const isDark = theme === 'dark';

  return (
    <div className={`p-6 sm:p-10 rounded-3xl border shadow-soft transition-colors duration-200 ${
      isDark ? 'bg-slate-900 border-slate-700/80 text-slate-200' : 'bg-white border-border text-slate-800'
    }`}>
      {/* Header kicker & titles */}
      <div className="space-y-1.5 pb-6 border-b border-border">
        <div className="text-xs font-extrabold uppercase tracking-widest text-amber-600 dark:text-amber-400">
          The Purpose Academy &bull; AI Impact Fellowship
        </div>
        <h1 className="text-2xl sm:text-3xl font-black text-navy dark:text-white">
          Python Basics - Reading Material
        </h1>
        <p className="text-sm sm:text-base font-bold text-primary dark:text-blue-400">
          Read this before the practice &bull; Lists to Loops
        </p>
      </div>

      {/* Intro Box */}
      <div className="my-6 p-4 sm:p-5 rounded-2xl border bg-blue-50/80 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800/60 text-xs sm:text-sm leading-relaxed text-slate-700 dark:text-slate-300">
        <strong className="text-primary dark:text-blue-300">How to use this page: </strong>
        Read it top to bottom - the topics build on each other. Try every green <strong>"Try it"</strong> in Google Colab as you go; five minutes of doing beats an hour of reading. This page covers everything you need for the coding practice that follows, including the exact way to read input for those problems (see the last section). When you reach the end, you are ready to start the questions.
      </div>

      {/* Table of Contents */}
      <div className="my-6 p-4 sm:p-5 rounded-2xl border bg-gray-50 dark:bg-slate-800/40 border-border">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3 flex items-center gap-1.5">
          <Terminal size={14} className="text-primary" /> What's inside
        </h3>
        <ol className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300 list-decimal list-inside">
          <li>Getting started with Colab</li>
          <li>Output with <code className="px-1 py-0.5 rounded bg-gray-200 dark:bg-slate-700 font-mono text-[11px]">print()</code></li>
          <li>Input with <code className="px-1 py-0.5 rounded bg-gray-200 dark:bg-slate-700 font-mono text-[11px]">input()</code></li>
          <li>Variables &amp; data types</li>
          <li>Operators</li>
          <li>Conditions (if / elif / else)</li>
          <li>Loops (for / while)</li>
          <li>Lists</li>
          <li>Functions</li>
          <li>Reading input for practice</li>
          <li>Quick self-check</li>
        </ol>
      </div>

      {/* 01. Getting Started */}
      <section className="my-8 pt-4 border-t border-border">
        <h2 className="text-lg sm:text-xl font-bold text-navy dark:text-white mb-2 flex items-center gap-2">
          <span className="text-xs font-extrabold px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300">01</span>
          Getting Started With Google Colab
        </h2>
        <p className="text-xs sm:text-sm leading-relaxed text-slate-600 dark:text-slate-300 mb-3">
          Python is a language for giving a computer clear instructions. You don't need to install anything - we use <strong>Google Colab</strong>, which runs Python in your browser. Type instructions into a <strong>code cell</strong> and press the <strong>Play</strong> button (or <code className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-800 font-mono">Shift + Enter</code>). The answer appears just below, in the output area.
        </p>
        <div className="p-3.5 rounded-xl border bg-emerald-50/80 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800/60 text-xs sm:text-sm text-emerald-900 dark:text-emerald-300">
          <strong>⌨ Try it:</strong> Open <code className="px-1.5 py-0.5 rounded bg-white dark:bg-slate-900 font-mono border border-emerald-300 dark:border-emerald-700">colab.research.google.com</code> &rarr; New notebook &rarr; type <code className="px-1.5 py-0.5 rounded bg-white dark:bg-slate-900 font-mono border border-emerald-300 dark:border-emerald-700">print("I am ready!")</code> &rarr; run.
        </div>
      </section>

      {/* 02. Output */}
      <section className="my-8 pt-4 border-t border-border">
        <h2 className="text-lg sm:text-xl font-bold text-navy dark:text-white mb-2 flex items-center gap-2">
          <span className="text-xs font-extrabold px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300">02</span>
          Output With <code className="font-mono text-base">print()</code>
        </h2>
        <p className="text-xs sm:text-sm leading-relaxed text-slate-600 dark:text-slate-300 mb-3">
          <code className="px-1 py-0.5 rounded bg-gray-100 dark:bg-slate-800 font-mono">print()</code> shows whatever is inside the brackets. Text must be in quotes. Separate several items with commas and Python adds a space between them.
        </p>
        <div className="my-3 rounded-xl overflow-hidden border border-border">
          <pre className="p-4 bg-slate-950 text-emerald-300 font-mono text-xs sm:text-sm leading-relaxed border-l-4 border-primary">
            {`print("Hello, World!")\nprint("Your score is", 100)`}
          </pre>
          <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 border-t border-emerald-200 dark:border-emerald-800 text-xs font-mono text-emerald-900 dark:text-emerald-300">
            <span className="text-[10px] font-bold uppercase tracking-wider block text-emerald-700 dark:text-emerald-400 mb-1">Output</span>
            Hello, World!<br />Your score is 100
          </div>
        </div>
        <div className="p-3 rounded-xl border bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 text-xs sm:text-sm text-amber-900 dark:text-amber-300">
          <strong>💡 Remember:</strong> text needs quotes; numbers don't.
        </div>
      </section>

      {/* 03. Input */}
      <section className="my-8 pt-4 border-t border-border">
        <h2 className="text-lg sm:text-xl font-bold text-navy dark:text-white mb-2 flex items-center gap-2">
          <span className="text-xs font-extrabold px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300">03</span>
          Input With <code className="font-mono text-base">input()</code>
        </h2>
        <p className="text-xs sm:text-sm leading-relaxed text-slate-600 dark:text-slate-300 mb-3">
          <code className="px-1 py-0.5 rounded bg-gray-100 dark:bg-slate-800 font-mono">input()</code> pauses the program and waits for the user to type. Whatever they type is stored in a variable. <strong>Important:</strong> <code className="font-mono">input()</code> always gives back <em>text</em> - wrap it in <code className="font-mono">int(...)</code> when you need a number.
        </p>
        <div className="my-3 rounded-xl overflow-hidden border border-border">
          <pre className="p-4 bg-slate-950 text-emerald-300 font-mono text-xs sm:text-sm leading-relaxed border-l-4 border-primary">
            {`name = input("Enter your name: ")\nage = int(input("Enter your age: "))\nprint("Hi", name, "- next year you are", age + 1)`}
          </pre>
        </div>
        <div className="p-3 rounded-xl border bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 text-xs sm:text-sm text-amber-900 dark:text-amber-300">
          <strong>💡 Remember:</strong> use <code className="font-mono">int(input())</code> for whole numbers and <code className="font-mono">float(input())</code> for decimals.
        </div>
      </section>

      {/* 04. Variables & Data Types */}
      <section className="my-8 pt-4 border-t border-border">
        <h2 className="text-lg sm:text-xl font-bold text-navy dark:text-white mb-2 flex items-center gap-2">
          <span className="text-xs font-extrabold px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300">04</span>
          Variables &amp; Data Types
        </h2>
        <p className="text-xs sm:text-sm leading-relaxed text-slate-600 dark:text-slate-300 mb-3">
          A variable is a labelled box that stores a value under a name, using <code className="font-mono">=</code>. Every value has a type:
        </p>
        <div className="my-3 overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-xs sm:text-sm">
            <thead>
              <tr className="bg-navy text-white text-left">
                <th className="p-2.5">Type</th>
                <th className="p-2.5">Example</th>
                <th className="p-2.5">Used for</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              <tr className="even:bg-gray-50 dark:even:bg-slate-800/40">
                <td className="p-2.5 font-mono text-primary font-bold">str</td>
                <td className="p-2.5 font-mono">"Delhi"</td>
                <td className="p-2.5">Text, wrapped in quotes</td>
              </tr>
              <tr className="even:bg-gray-50 dark:even:bg-slate-800/40">
                <td className="p-2.5 font-mono text-primary font-bold">int</td>
                <td className="p-2.5 font-mono">18</td>
                <td className="p-2.5">Whole numbers</td>
              </tr>
              <tr className="even:bg-gray-50 dark:even:bg-slate-800/40">
                <td className="p-2.5 font-mono text-primary font-bold">float</td>
                <td className="p-2.5 font-mono">98.6</td>
                <td className="p-2.5">Decimal numbers</td>
              </tr>
              <tr className="even:bg-gray-50 dark:even:bg-slate-800/40">
                <td className="p-2.5 font-mono text-primary font-bold">bool</td>
                <td className="p-2.5 font-mono">True</td>
                <td className="p-2.5">Yes/no logic (True / False)</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="p-3 rounded-xl border bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 text-xs sm:text-sm text-amber-900 dark:text-amber-300">
          <strong>💡 Remember:</strong> <code className="font-mono">"18"</code> is text, but <code className="font-mono">18</code> is a number. Names have no spaces and cannot start with a digit.
        </div>
      </section>

      {/* 05. Operators */}
      <section className="my-8 pt-4 border-t border-border">
        <h2 className="text-lg sm:text-xl font-bold text-navy dark:text-white mb-2 flex items-center gap-2">
          <span className="text-xs font-extrabold px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300">05</span>
          Operators
        </h2>
        <p className="text-xs sm:text-sm leading-relaxed text-slate-600 dark:text-slate-300 mb-2">
          <strong>Arithmetic:</strong> <code className="font-mono">+ - * /</code> and <code className="font-mono">%</code> (remainder). Multiplication happens before addition.
        </p>
        <div className="my-3 rounded-xl overflow-hidden border border-border">
          <pre className="p-4 bg-slate-950 text-emerald-300 font-mono text-xs sm:text-sm leading-relaxed border-l-4 border-primary">
            {`print(7 + 2 * 3)`}
          </pre>
          <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 border-t border-emerald-200 dark:border-emerald-800 text-xs font-mono text-emerald-900 dark:text-emerald-300">
            <span className="text-[10px] font-bold uppercase tracking-wider block text-emerald-700 dark:text-emerald-400 mb-1">Output</span>
            13
          </div>
        </div>
        <p className="text-xs sm:text-sm leading-relaxed text-slate-600 dark:text-slate-300 my-2">
          <strong>Comparison</strong> gives True/False: <code className="font-mono">== != &gt; &lt; &gt;= &lt;=</code>. Logical: <code className="font-mono">and</code>, <code className="font-mono">or</code>, <code className="font-mono">not</code>.
        </p>
        <div className="p-3 rounded-xl border bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 text-xs sm:text-sm text-amber-900 dark:text-amber-300">
          <strong>💡 Remember:</strong> one <code className="font-mono">=</code> <strong>stores</strong> a value; two <code className="font-mono">==</code> <strong>compares</strong>. This is the #1 beginner slip.
        </div>
      </section>

      {/* 06. Conditions */}
      <section className="my-8 pt-4 border-t border-border">
        <h2 className="text-lg sm:text-xl font-bold text-navy dark:text-white mb-2 flex items-center gap-2">
          <span className="text-xs font-extrabold px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300">06</span>
          Conditions - if / elif / else
        </h2>
        <p className="text-xs sm:text-sm leading-relaxed text-slate-600 dark:text-slate-300 mb-3">
          <code className="font-mono">if</code> runs when a condition is True, <code className="font-mono">elif</code> checks another, and <code className="font-mono">else</code> is the fallback. Note the colon and the indentation.
        </p>
        <div className="my-3 rounded-xl overflow-hidden border border-border">
          <pre className="p-4 bg-slate-950 text-emerald-300 font-mono text-xs sm:text-sm leading-relaxed border-l-4 border-primary">
            {`marks = 85\nif marks >= 80:\n    print("Excellent")\nelif marks >= 33:\n    print("Pass")\nelse:\n    print("Fail")`}
          </pre>
          <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 border-t border-emerald-200 dark:border-emerald-800 text-xs font-mono text-emerald-900 dark:text-emerald-300">
            <span className="text-[10px] font-bold uppercase tracking-wider block text-emerald-700 dark:text-emerald-400 mb-1">Output</span>
            Excellent
          </div>
        </div>
        <div className="p-3 rounded-xl border bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 text-xs sm:text-sm text-amber-900 dark:text-amber-300">
          <strong>💡 Remember:</strong> Python checks conditions <strong>top to bottom and stops at the first True one</strong>, so order them strictest-first (<code className="font-mono">&gt;= 80</code> before <code className="font-mono">&gt;= 33</code>).
        </div>
      </section>

      {/* 07. Loops */}
      <section className="my-8 pt-4 border-t border-border">
        <h2 className="text-lg sm:text-xl font-bold text-navy dark:text-white mb-2 flex items-center gap-2">
          <span className="text-xs font-extrabold px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300">07</span>
          Loops - for / while
        </h2>
        <h3 className="text-sm sm:text-base font-bold text-primary dark:text-blue-400 my-2">for loop with range()</h3>
        <p className="text-xs sm:text-sm leading-relaxed text-slate-600 dark:text-slate-300 mb-2">
          <code className="font-mono">range(5)</code> counts 0,1,2,3,4 (starts at 0, stops before 5). <code className="font-mono">range(1, 5)</code> counts 1,2,3,4.
        </p>
        <div className="my-3 rounded-xl overflow-hidden border border-border">
          <pre className="p-4 bg-slate-950 text-emerald-300 font-mono text-xs sm:text-sm leading-relaxed border-l-4 border-primary">
            {`total = 0\nfor i in range(1, 5):\n    total = total + i\nprint(total)`}
          </pre>
          <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 border-t border-emerald-200 dark:border-emerald-800 text-xs font-mono text-emerald-900 dark:text-emerald-300">
            <span className="text-[10px] font-bold uppercase tracking-wider block text-emerald-700 dark:text-emerald-400 mb-1">Output</span>
            10
          </div>
        </div>

        <h3 className="text-sm sm:text-base font-bold text-primary dark:text-blue-400 my-2 pt-2">while loop</h3>
        <p className="text-xs sm:text-sm leading-relaxed text-slate-600 dark:text-slate-300 mb-2">
          Repeats while a condition stays True - you must change something so it can stop.
        </p>
        <div className="my-3 rounded-xl overflow-hidden border border-border">
          <pre className="p-4 bg-slate-950 text-emerald-300 font-mono text-xs sm:text-sm leading-relaxed border-l-4 border-primary">
            {`count = 1\nwhile count <= 3:\n    print("Go!")\n    count = count + 1`}
          </pre>
          <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 border-t border-emerald-200 dark:border-emerald-800 text-xs font-mono text-emerald-900 dark:text-emerald-300">
            <span className="text-[10px] font-bold uppercase tracking-wider block text-emerald-700 dark:text-emerald-400 mb-1">Output</span>
            Go!<br />Go!<br />Go!
          </div>
        </div>
        <div className="p-3 rounded-xl border bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 text-xs sm:text-sm text-amber-900 dark:text-amber-300">
          <strong>💡 Remember:</strong> <code className="font-mono">range(n)</code> stops just before <code className="font-mono">n</code>. In a while loop, always update the counter.
        </div>
      </section>

      {/* 08. Lists */}
      <section className="my-8 pt-4 border-t border-border">
        <h2 className="text-lg sm:text-xl font-bold text-navy dark:text-white mb-2 flex items-center gap-2">
          <span className="text-xs font-extrabold px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300">08</span>
          Lists
        </h2>
        <p className="text-xs sm:text-sm leading-relaxed text-slate-600 dark:text-slate-300 mb-3">
          A list holds many values in order, written in square brackets. Positions (indexes) start at <strong>0</strong>; <code className="font-mono">-1</code> is the last item.
        </p>
        <div className="my-3 rounded-xl overflow-hidden border border-border">
          <pre className="p-4 bg-slate-950 text-emerald-300 font-mono text-xs sm:text-sm leading-relaxed border-l-4 border-primary">
            {`subjects = ["Math", "Science", "English"]\nprint(subjects[0])      # first\nprint(subjects[-1])     # last\nsubjects.append("CS")   # add to the end\nfor s in subjects:\n    print(s)`}
          </pre>
          <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 border-t border-emerald-200 dark:border-emerald-800 text-xs font-mono text-emerald-900 dark:text-emerald-300">
            <span className="text-[10px] font-bold uppercase tracking-wider block text-emerald-700 dark:text-emerald-400 mb-1">Output</span>
            Math<br />English<br />Math<br />Science<br />English<br />CS
          </div>
        </div>
        <div className="p-3 rounded-xl border bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 text-xs sm:text-sm text-amber-900 dark:text-amber-300">
          <strong>💡 Remember:</strong> first item is <code className="font-mono">list[0]</code>; add with <code className="font-mono">.append()</code>; count with <code className="font-mono">len(list)</code>.
        </div>
      </section>

      {/* 09. Functions */}
      <section className="my-8 pt-4 border-t border-border">
        <h2 className="text-lg sm:text-xl font-bold text-navy dark:text-white mb-2 flex items-center gap-2">
          <span className="text-xs font-extrabold px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300">09</span>
          Functions
        </h2>
        <p className="text-xs sm:text-sm leading-relaxed text-slate-600 dark:text-slate-300 mb-3">
          A function is a reusable, named block of code. Define it with <code className="font-mono">def</code>; send a value back with <code className="font-mono">return</code>.
        </p>
        <div className="my-3 rounded-xl overflow-hidden border border-border">
          <pre className="p-4 bg-slate-950 text-emerald-300 font-mono text-xs sm:text-sm leading-relaxed border-l-4 border-primary">
            {`def add(a, b):\n    return a + b\n\nprint(add(8, 5))`}
          </pre>
          <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 border-t border-emerald-200 dark:border-emerald-800 text-xs font-mono text-emerald-900 dark:text-emerald-300">
            <span className="text-[10px] font-bold uppercase tracking-wider block text-emerald-700 dark:text-emerald-400 mb-1">Output</span>
            13
          </div>
        </div>
      </section>

      {/* 10. Reading Input */}
      <section className="my-8 pt-4 border-t border-border">
        <h2 className="text-lg sm:text-xl font-bold text-navy dark:text-white mb-2 flex items-center gap-2">
          <span className="text-xs font-extrabold px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300">10</span>
          Reading Input for Practice Problems
        </h2>
        <p className="text-xs sm:text-sm leading-relaxed text-slate-600 dark:text-slate-300 mb-3">
          The practice questions are auto-graded: your program <strong>reads the given input</strong> and <strong>prints the exact output</strong>. Here are the only four input patterns you will need - keep this section handy.
        </p>

        <div className="space-y-4 my-3">
          <div>
            <h4 className="font-bold text-xs sm:text-sm text-navy dark:text-white mb-1">One number on a line</h4>
            <pre className="p-3 bg-slate-950 text-emerald-300 font-mono text-xs rounded-xl border border-slate-800">
              {`n = int(input())`}
            </pre>
          </div>

          <div>
            <h4 className="font-bold text-xs sm:text-sm text-navy dark:text-white mb-1">Two numbers on one line (separated by a space)</h4>
            <pre className="p-3 bg-slate-950 text-emerald-300 font-mono text-xs rounded-xl border border-slate-800">
              {`a, b = input().split()\na = int(a)\nb = int(b)\nprint(a + b)`}
            </pre>
          </div>

          <div>
            <h4 className="font-bold text-xs sm:text-sm text-navy dark:text-white mb-1">A whole line of numbers into a list</h4>
            <pre className="p-3 bg-slate-950 text-emerald-300 font-mono text-xs rounded-xl border border-slate-800">
              {`nums = list(map(int, input().split()))\nprint(nums)        # e.g. [45, 78, 90, 33]`}
            </pre>
          </div>

          <div>
            <h4 className="font-bold text-xs sm:text-sm text-navy dark:text-white mb-1">Printing a list back on one line (space-separated)</h4>
            <pre className="p-3 bg-slate-950 text-emerald-300 font-mono text-xs rounded-xl border border-slate-800">
              {`nums = [4, 3, 2, 1]\nprint(" ".join(map(str, nums)))`}
            </pre>
            <div className="p-2.5 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-xs font-mono text-emerald-900 dark:text-emerald-300 rounded-xl mt-1.5">
              4 3 2 1
            </div>
          </div>
        </div>

        <div className="p-3 rounded-xl border bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 text-xs sm:text-sm text-amber-900 dark:text-amber-300">
          <strong>💡 Remember:</strong> print <strong>exactly</strong> what is asked - no extra words or spaces. If the task says print the number only, print just the number.
        </div>
      </section>

      {/* 11. Quick Self-Check */}
      <section className="my-8 pt-4 border-t border-border">
        <h2 className="text-lg sm:text-xl font-bold text-navy dark:text-white mb-2 flex items-center gap-2">
          <span className="text-xs font-extrabold px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300">11</span>
          Quick Self-Check
        </h2>
        <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mb-3">
          Answer in your head, then click to reveal and check.
        </p>

        <div className="space-y-2">
          <details className="p-3 rounded-xl border border-border bg-gray-50/60 dark:bg-slate-800/40 cursor-pointer">
            <summary className="font-bold text-xs sm:text-sm text-navy dark:text-white">
              1) What does <code className="font-mono text-xs text-primary">print(2 + 3 * 2)</code> display?
            </summary>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 pt-2 pl-4 border-l-2 border-primary mt-2">
              <strong>8</strong> - Python does 3 &times; 2 first (6), then adds 2 (8).
            </p>
          </details>

          <details className="p-3 rounded-xl border border-border bg-gray-50/60 dark:bg-slate-800/40 cursor-pointer">
            <summary className="font-bold text-xs sm:text-sm text-navy dark:text-white">
              2) Which is text: <code className="font-mono text-xs text-primary">18</code> or <code className="font-mono text-xs text-primary">"18"</code>?
            </summary>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 pt-2 pl-4 border-l-2 border-primary mt-2">
              <code className="font-mono">"18"</code> is text (a string). <code className="font-mono">18</code> is a number (integer).
            </p>
          </details>

          <details className="p-3 rounded-xl border border-border bg-gray-50/60 dark:bg-slate-800/40 cursor-pointer">
            <summary className="font-bold text-xs sm:text-sm text-navy dark:text-white">
              3) What numbers does <code className="font-mono text-xs text-primary">range(1, 4)</code> produce?
            </summary>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 pt-2 pl-4 border-l-2 border-primary mt-2">
              <strong>1, 2, 3</strong> - it stops before 4.
            </p>
          </details>

          <details className="p-3 rounded-xl border border-border bg-gray-50/60 dark:bg-slate-800/40 cursor-pointer">
            <summary className="font-bold text-xs sm:text-sm text-navy dark:text-white">
              4) Which symbol compares two values - <code className="font-mono text-xs text-primary">=</code> or <code className="font-mono text-xs text-primary">==</code>?
            </summary>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 pt-2 pl-4 border-l-2 border-primary mt-2">
              <code className="font-mono">==</code> compares; <code className="font-mono">=</code> assigns/stores a value.
            </p>
          </details>

          <details className="p-3 rounded-xl border border-border bg-gray-50/60 dark:bg-slate-800/40 cursor-pointer">
            <summary className="font-bold text-xs sm:text-sm text-navy dark:text-white">
              5) How do you add <code className="font-mono text-xs text-primary">"Mango"</code> to a list called <code className="font-mono text-xs text-primary">fruits</code>?
            </summary>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 pt-2 pl-4 border-l-2 border-primary mt-2">
              <code className="font-mono">fruits.append("Mango")</code>
            </p>
          </details>
        </div>
      </section>

      {/* Footer Callout */}
      <div className="mt-10 pt-6 border-t-2 border-primary text-xs sm:text-sm text-slate-700 dark:text-slate-300 flex items-start gap-3">
        <Sparkles size={24} className="text-amber-500 shrink-0 mt-0.5" />
        <div>
          <strong className="text-navy dark:text-white block text-sm sm:text-base mb-0.5">
            You are ready! 🚀
          </strong>
          Head to the practice questions and solve them in order - they follow the same path you just read. Keep this reference handy while you code, and remember: mistakes are how you learn.
        </div>
      </div>
    </div>
  );
};

export default PythonBasicsReadingMaterial;
